import React, { useCallback, useRef } from "react";
import "./MembershipGuide.scss";
import {
  DEFAULT_HERO_SUBHEAD,
  DEFAULT_PRIMARY_CTA,
  HERO_PHONE_LISTINGS,
  PROCESS_STEPS,
} from "./membershipGuideConst";
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  SearchIcon,
} from "./membershipGuideIcons";
import Features from "./components/Features";
import PlanTable from "./components/PlanTable";
import ConsultForm from "./components/ConsultForm";
import Faq from "./components/Faq";

const NAV_HEIGHT = 64;

const NAV_LINKS = [
  { id: "features", label: "주요 기능" },
  { id: "plans", label: "멤버십 요금" },
  { id: "process", label: "이용 방법" },
  { id: "faq", label: "자주 묻는 질문" },
];

const PILL_VARIANT_CLASS = { orange: "orange", blue: "blue" };

const goBack = () => {
  if (window.bh?.go_before) {
    window.bh.go_before();
    return;
  }
  if (window.history.length > 1) {
    window.history.back();
  }
};

const Nav = ({ onOpenConsult, onScrollTo }) => {
  const handleScroll = (id) => (ev) => {
    ev.preventDefault();
    onScrollTo(id);
  };

  return (
    <nav className="nav">
      <div className="container nav-inner">
        <button type="button" className="nav-back" aria-label="뒤로가기" onClick={goBack}>
          <ChevronLeftIcon />
        </button>
        <a className="nav-logo" href="#top" onClick={handleScroll("top")}>
          <span className="nav-logo-sub">디스코 멤버십</span>
        </a>
        <div className="nav-links">
          {NAV_LINKS.map(({ id, label }) => (
            <a key={id} href={`#${id}`} onClick={handleScroll(id)}>
              {label}
            </a>
          ))}
        </div>
        <div className="nav-cta">
          <button type="button" className="btn btn-primary" onClick={onOpenConsult}>
            상담 예약하기
          </button>
        </div>
      </div>
    </nav>
  );
};

const PhoneListing = ({ rank, pills, price, addr, dim }) => (
  <div className="hv-listing" style={dim ? { opacity: 0.55 } : undefined}>
    <div className={`hv-thumb${rank ? ` rank${rank}` : ""}`} />
    <div className="hv-info">
      <div className="hv-info-pills">
        {pills.map((pill, i) => (
          <span
            key={i}
            className={`hv-info-pill ${PILL_VARIANT_CLASS[pill.variant] || ""}`.trim()}
            style={pill.variant === "muted" ? { background: "#F3F4F6", color: "#6B7280" } : undefined}
          >
            {pill.label}
          </span>
        ))}
      </div>
      <div className="hv-info-price" style={dim ? { color: "#6B7280" } : undefined}>
        {price}
      </div>
      <div className="hv-info-addr">{addr}</div>
    </div>
  </div>
);

const Hero = ({ subhead, primaryCta, onOpenConsult, onScrollTo }) => (
  <section className="hero" id="top">
    <div className="container hero-inner">
      <div>
        <h1>
          <span className="lead">1등 중개사가 되는 가장 빠른 길,</span>
          <span className="brand">디스코 멤버십</span>
        </h1>
        <p className="sub">{subhead}</p>
        <div className="hero-ctas">
          <button type="button" className="btn btn-primary btn-lg" onClick={onOpenConsult}>
            {primaryCta}
            <ArrowRightIcon />
          </button>
          <a
            href="#plans"
            className="btn btn-secondary btn-lg"
            onClick={(ev) => {
              ev.preventDefault();
              onScrollTo("plans");
            }}
          >
            멤버십 요금 보기
          </a>
        </div>
      </div>
      <div className="hero-visual">
        <div className="hv-floating-callout">
          실시간 노출 중 · <strong>1순위</strong>
        </div>
        <div className="hv-phone">
          <div className="hv-topbar">
            <span>경기도 성남시 판교동</span>
            <SearchIcon />
          </div>
          <div className="hv-tabs">
            <div className="hv-tab active">매물</div>
            <div className="hv-tab">실거래가</div>
            <div className="hv-tab">중개사</div>
          </div>
          <div className="hv-listings">
            {HERO_PHONE_LISTINGS.map((listing, i) => (
              <PhoneListing key={i} {...listing} />
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section className="section section-white" id="process">
    <div className="container">
      <div className="section-title-row">
        <h2>
          <span className="num">03</span> 이용 방법
        </h2>
      </div>
      <div className="process">
        {PROCESS_STEPS.map((step) => (
          <div key={step.title} className="step">
            <h3>{step.title}</h3>
            <p>{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="footer">
    <div className="container">
      <div className="footer-bottom">
        <div>© {new Date().getFullYear()} 주식회사 디스코. All rights reserved.</div>
      </div>
    </div>
  </footer>
);

const MembershipGuide = ({
  heroSubhead = DEFAULT_HERO_SUBHEAD,
  primaryCta = DEFAULT_PRIMARY_CTA,
}) => {
  const formRef = useRef(null);
  const pageRef = useRef(null);

  const scrollTo = useCallback((id) => {
    const container = pageRef.current;
    if (!container) return;
    if (id === "top") {
      container.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const target = container.querySelector(`#${id}`);
    if (!target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = targetRect.top - containerRect.top + container.scrollTop - NAV_HEIGHT;
    container.scrollTo({ top, behavior: "smooth" });
  }, []);

  const scrollToConsult = useCallback(() => scrollTo("consult"), [scrollTo]);

  return (
    <div className="membership-guide-page" ref={pageRef}>
      <Nav onOpenConsult={scrollToConsult} onScrollTo={scrollTo} />
      <Hero
        subhead={heroSubhead}
        primaryCta={primaryCta}
        onOpenConsult={scrollToConsult}
        onScrollTo={scrollTo}
      />
      <Features />
      <PlanTable onOpenConsult={scrollToConsult} />
      <Process />
      <ConsultForm formRef={formRef} />
      <Faq />
      <Footer />
    </div>
  );
};

export default MembershipGuide;
